import { supabase } from '../lib/supabaseClient';
import type { JornadaParaBaja, ResultadoBaja } from '../domain/bajasDeTurnos';

const PAGE_SIZE = 1000;

type Raw = {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  slot_index: number;
  pdv: { id: string; code: string; name: string } | null;
  assignments: {
    status: string;
    worker: { first_names: string; last_names: string } | null;
    attendance: { id: string }[];
  }[];
};

/**
 * Todas las jornadas de la campaña, con o sin persona, para la pestaña «Bajas» (0399). Las rechazadas no cuentan como
 * persona del turno, pero una marca en CUALQUIER asignación sí bloquea la baja (el servidor decide lo mismo).
 *
 * Como en las otras pestañas, una lectura incompleta LANZA: es mejor no listar que listar de menos.
 */
export async function listarJornadasParaBaja(campaignId: string): Promise<JornadaParaBaja[]> {
  const filas: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += PAGE_SIZE) {
    // Hint de FK obligatorio: `assignments` tiene tres FK a `profiles`.
    const { data, error, count } = await supabase
      .from('positions')
      .select(
        `id, date, start_time, end_time, slot_index,
        pdv:pdv(id, code, name),
        assignments(status, worker:profiles!assignments_worker_id_fkey(first_names, last_names), attendance(id))`,
        { count: 'exact' },
      )
      .eq('campaign_id', campaignId)
      .order('date')
      .order('start_time')
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    if (count !== null) total = count;
    const lote = (data ?? []) as unknown as Raw[];
    filas.push(...lote);
    if (lote.length < PAGE_SIZE) break;
  }
  if (total !== null && filas.length !== total) {
    throw new Error(`Lectura incompleta: se recibieron ${filas.length} de ${total} jornadas. Vuelve a cargar.`);
  }
  return filas.map((r) => ({
    id: r.id,
    date: r.date,
    start_time: r.start_time,
    end_time: r.end_time,
    slot_index: r.slot_index,
    pdv: r.pdv,
    personas: r.assignments
      .filter((a) => a.status === 'propuesta' || a.status === 'aprobada')
      .map((a) => ({
        nombre: a.worker ? `${a.worker.first_names} ${a.worker.last_names}` : 'Persona no visible',
        estado: a.status,
      })),
    conMarca: r.assignments.some((a) => (a.attendance ?? []).length > 0),
  }));
}

export async function darDeBajaJornadas(ids: string[], motivo: string): Promise<ResultadoBaja> {
  const { data, error } = await supabase.rpc('dar_de_baja_jornadas', { p_ids: ids, p_motivo: motivo });
  if (error) throw new Error(error.message);
  return data as ResultadoBaja;
}

export interface BajaVigente {
  id: string;
  fecha: string;
  start_time: string;
  end_time: string;
  slot_index: number;
  motivo: string;
  dada_en: string;
  pdv: { code: string; name: string } | null;
}

/** Las bajas vigentes de la campaña (las reactivadas ya no se listan). */
export async function listarBajas(campaignId: string): Promise<BajaVigente[]> {
  const { data, error } = await supabase
    .from('jornadas_dadas_de_baja')
    .select('id, fecha, start_time, end_time, slot_index, motivo, dada_en, pdv:pdv(code, name)')
    .eq('campaign_id', campaignId)
    .is('reactivada_en', null)
    .order('fecha')
    .order('start_time');
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as BajaVigente[];
}

export async function reactivarJornada(bajaId: string): Promise<void> {
  const { error } = await supabase.rpc('reactivar_jornada', { p_baja: bajaId });
  if (error) throw new Error(error.message);
}
